'use client';

import * as React from 'react';
import styles from './agentAvatar.module.css';

type AnimationState = 'idle' | 'looking-up' | 'blinking';
type Frame = { svg: string; duration: number };

const LOOK_UP_SEQUENCE: Frame[] = [
  { svg: 'up-01', duration: 100 },
  { svg: 'up-02', duration: 2000 },
  { svg: 'up-01', duration: 100 },
  { svg: 'default', duration: 0 },
];

const BLINK_SEQUENCE: Frame[] = [
  { svg: 'blink-01', duration: 50 },
  { svg: 'blink-02', duration: 100 },
  { svg: 'blink-01', duration: 50 },
  { svg: 'default', duration: 0 },
];

// Always use the lightMode SVG variant so Fulton's stroke matches
// `var(--agent-avatar-stroke)` — locked to the light-foreground hex in both
// themes (see `globals.css`). The dark-mode inversion is done at the wrapper
// level via `var(--agent-avatar-bg)` (white circle), not by swapping the SVG.
// The darkMode SVG files stay on disk in case the design reverses later, but
// only the lightMode variant is used here.
function getAgentSvgPath(name: string): string {
  const fileName = name === 'default' ? 'default-lightMode' : `${name}-lightMode`;
  return `/logo/fulton/lightMode/${fileName}.svg`;
}

export interface AgentAvatarProps {
  /** Size of the avatar (affects both width and height) */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** When true, pauses all animations */
  paused?: boolean;
  /** Optional className for additional styling */
  className?: string;
  /** When true, shows the white circular background in any theme (default off). */
  hasBackground?: boolean;
}

export function AgentAvatar({
  size = 'md',
  paused = false,
  className,
  hasBackground = false,
}: AgentAvatarProps) {
  const [currentSvg, setCurrentSvg] = React.useState('default');
  const [animationState, setAnimationState] = React.useState<AnimationState>('idle');
  const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const scheduleTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const playSequence = React.useCallback((frames: Frame[], index = 0) => {
    const step = (i: number) => {
      if (i >= frames.length) {
        setAnimationState('idle');
        setCurrentSvg('default');
        return;
      }

      const frame = frames[i];
      setCurrentSvg(frame.svg);

      if (frame.duration > 0) {
        animationTimeoutRef.current = setTimeout(() => {
          step(i + 1);
        }, frame.duration);
      } else {
        step(i + 1);
      }
    };
    step(index);
  }, []);

  const playLookUpAnimation = React.useCallback(() => {
    if (animationState !== 'idle') return;
    setAnimationState('looking-up');
    playSequence(LOOK_UP_SEQUENCE);
  }, [animationState, playSequence]);

  const playBlinkAnimation = React.useCallback(() => {
    if (animationState !== 'idle') return;
    setAnimationState('blinking');
    playSequence(BLINK_SEQUENCE);
  }, [animationState, playSequence]);

  const scheduleNextAnimation = React.useCallback(() => {
    if (paused) return;

    const minDelay = 3000;
    const maxDelay = 8000;
    const delay = minDelay + Math.random() * (maxDelay - minDelay);

    scheduleTimeoutRef.current = setTimeout(() => {
      // 60% chance of blink, 40% chance of look up (slightly more blinks)
      const shouldBlink = Math.random() < 0.6;
      if (shouldBlink) {
        playBlinkAnimation();
      } else {
        playLookUpAnimation();
      }
    }, delay);
  }, [paused, playLookUpAnimation, playBlinkAnimation]);

  // Schedule next animation when returning to idle state
  React.useEffect(() => {
    if (animationState === 'idle' && !paused) {
      scheduleNextAnimation();
    }

    return () => {
      if (scheduleTimeoutRef.current) {
        clearTimeout(scheduleTimeoutRef.current);
      }
    };
  }, [animationState, paused, scheduleNextAnimation]);

  // Clean up animation timeout on unmount or pause
  React.useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (scheduleTimeoutRef.current) {
        clearTimeout(scheduleTimeoutRef.current);
      }
    };
  }, []);

  // Reset to default when paused
  React.useEffect(() => {
    if (paused) {
      // Intentional one-shot reset when the `paused` prop flips on — syncing to
      // external prop state, not a value derivable during render.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset-on-pause sync to prop
      setCurrentSvg('default');
      setAnimationState('idle');
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (scheduleTimeoutRef.current) {
        clearTimeout(scheduleTimeoutRef.current);
      }
    }
  }, [paused]);

  const src = getAgentSvgPath(currentSvg);

  return (
    <div
      className={`${styles.root} ${className || ''}`.trim()}
      data-size={size}
      data-has-background={hasBackground}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- rapidly swapped local SVG animation frames; next/image adds no optimization for inline SVGs and its loader churns on every frame */}
      <img
        src={src}
        alt="Fulton"
        className={styles.image}
        draggable={false}
      />
    </div>
  );
}

export default AgentAvatar;
